import { useEffect } from 'react';
import { useLocation } from 'react-router';

// How long to keep looking for a #section that isn't on the page yet
// (e.g. a page still loading): 20 tries x 50ms = 1 second.
const MAX_TRIES = 20;
const RETRY_MS = 50;

/**
 * Scrolls to the element whose id matches the URL hash.
 * Returns a cleanup function that cancels any pending attempt.
 */
function scrollToHash(hash) {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    let tries = 0;
    let timer;

    const attempt = () => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ block: 'start' });
            return;
        }
        tries += 1;
        if (tries < MAX_TRIES) timer = setTimeout(attempt, RETRY_MS);
    };

    // Wait one frame so the new page has painted before measuring.
    const frame = requestAnimationFrame(attempt);

    return () => {
        cancelAnimationFrame(frame);
        clearTimeout(timer);
    };
}

/**
 * On every navigation:
 * - a link with a #section (e.g. /solution#money-out) scrolls to that section;
 * - any other link scrolls to the top of the page, as before.
 */
function ScrollOnRouteChange({ children }) {
    const { pathname, hash, key } = useLocation();

    useEffect(() => {
        if (hash) return scrollToHash(hash);
        window.scrollTo(0, 0);
        return undefined;
    }, [pathname, hash, key]);

    return children;
}

export default ScrollOnRouteChange;